import { MentionCard, MentionV4 } from "@/types/MentionCard";
import { getIsSocialMediaMention } from "@/views/reports/helpers";
import { Card } from "@components/ui/card";
import { AudioPlayer } from "./partials/AudioPlayer";
import { MentionItem } from "./partials/MentionItem";
import { MentionCardFooter } from "./partials/MentionCardFooter";
import { MediaProvider } from "media-chrome/react/media-store";
import { Tag } from "@/types/Tag";
import { Organization } from "@/types/Organization";
import { useState } from "react";
import { TranslationResponse } from "@/api/endpoints/translate";
import { Sentiment, TRANSCRIPT_STATUS } from "@/constants";
import Cover from "./partials/Cover";
import { Alert, AlertTitle } from "@components/ui/alert";
import { InfoIcon } from "lucide-react";
import { Trans } from "@lingui/react/macro";
import { postTrackingData } from "@/api/endpoints/track";
import { EVENT } from "@/tracking_events";
import TranscriptStatus from "./partials/TranscriptStatus";
import Header from "./partials/Header";
import { categorizeMentionsByContext } from "@/lib/mention";
import { useUser } from "@/hooks/useUser";
import { User } from "@/types/User";

type MentionCardProps = {
  orgUuid: Organization["uuid"];
  mentionCard: MentionCard;
  tags: Tag[];
};

function MentionCardNewComponent({
  mentionCard,
  orgUuid,
  tags,
}: MentionCardProps) {
  const { data } = useUser();
  const is_staff = (data?.data as User)?.is_staff || false;

  // Local state
  const [localMentions, setLocalMentions] = useState<MentionV4[]>(
    mentionCard.mentions
  );

  const [localSentiment, setLocalSentiment] = useState<
    MentionCard["aggregated_sentiment"]
  >(mentionCard.aggregated_sentiment);

  const [localTranscriptStatus, setLocalTranscriptStatus] = useState(
    mentionCard.episode.transcript_status
  );

  if (!localMentions.length) {
    return null;
  }

  // Helpers
  const totalMentionsCount = localMentions.length;

  const isSocialMediaMention = getIsSocialMediaMention(
    mentionCard.channel.channel_type
  );

  const { titleMention, otherMentions: mentions } =
    categorizeMentionsByContext(localMentions);

  const mentionsCount = mentions.length;

  const hasAudioMentions = mentions.some(
    (mention) => typeof mention.timing === "number"
  );

  // Handlers
  function handleAfterDeleteMention(mentionId: MentionV4["id"]) {
    setLocalMentions((prev) => prev.filter((m) => m.id !== mentionId));
  }

  function handleAfterTranslateMention(
    mentionId: MentionV4["id"],
    translatedText: TranslationResponse["translated_text"]
  ) {
    setLocalMentions((prev) =>
      prev.map((m) =>
        m.id === mentionId
          ? { ...m, translated_text: translatedText, translated: true }
          : m
      )
    );
  }

  function handleRevertMentionTranslation(mentionId: MentionV4["id"]) {
    setLocalMentions((prev) =>
      prev.map((m) =>
        m.id === mentionId
          ? { ...m, translated_text: undefined, translated: false }
          : m
      )
    );
  }

  function handleAfterChangeMentionSentiment(
    mentionId: MentionV4["id"],
    newSentiment: Sentiment,
    newAggregatedSentiment: Sentiment
  ) {
    setLocalMentions((prev) =>
      prev.map((m) =>
        m.id === mentionId ? { ...m, sentiment: newSentiment } : m
      )
    );

    setLocalSentiment(newAggregatedSentiment);
  }

  function handleAfterChangeAggregatedSentiment(
    newAggregatedSentiment: Sentiment
  ) {
    setLocalMentions((prev) =>
      prev.map((m) => ({ ...m, sentiment: newAggregatedSentiment }))
    );

    setLocalSentiment(newAggregatedSentiment);
  }

  function handleAfterDelete() {
    setLocalMentions([]);
  }

  function handleAfterRequestTranscribe() {
    setLocalTranscriptStatus(TRANSCRIPT_STATUS.QUEUED_FOR_TRANSCRIPTION);
  }

  const hasIrrelevantMentions =
    is_staff &&
    localMentions.some(
      (mention) => mention.llm_relevance != null && mention.llm_relevance < 0.5
    );

  return (
    <Card
      id={mentionCard.mentions[0].public_id}
      data-testid="episode-container"
      className="pb-0 pt-4 relative"
    >
      {hasIrrelevantMentions && (
        <div className="absolute inset-0 bg-blue-100/60 z-10 rounded-xl pointer-events-none" />
      )}
      <div className="flex flex-col px-4 gap-2">
        <div className="flex flex-col-reverse lg:flex-row items-start gap-4 lg:gap-8">
          <div className="flex flex-col gap-1 flex-1">
            <div className="flex flex-col gap-2">
              <Header
                channelType={mentionCard.channel.channel_type}
                externalId={mentionCard.channel.external_id}
                websiteUrl={mentionCard.channel.website_url}
                audioUrl={mentionCard.episode.audio_url}
                totalMentionsCount={totalMentionsCount}
                channelName={mentionCard.channel.name}
                topicNames={mentionCard.topic_names}
                shareUrl={mentionCard.share_url}
              />

              <div className="flex flex-col gap-1.5">
                {!isSocialMediaMention &&
                  (mentionCard.episode.title || titleMention) &&
                  (titleMention ? (
                    <p
                      data-testid="episode-title"
                      className="text-lg font-semibold leading-normal text-balance highlight-match"
                      dangerouslySetInnerHTML={{
                        __html: titleMention.display_text,
                      }}
                    />
                  ) : (
                    <p
                      data-testid="episode-title"
                      className="text-lg font-semibold leading-normal text-balance"
                    >
                      {mentionCard.episode.title}
                    </p>
                  ))}
              </div>
            </div>

            <div className="flex-1 flex flex-col gap-2.5">
              {hasAudioMentions &&
                (mentionCard.episode.audio_url ? (
                  <AudioPlayer
                    grouped_times_and_words={
                      mentionCard.grouped_times_and_words
                    }
                    length_seconds={mentionCard.episode.length_seconds}
                    audio_url={mentionCard.episode.audio_url}
                    episode_id={mentionCard.episode.id}
                    mentions={mentions}
                    onPlay={() => {
                      postTrackingData(EVENT.PLAYED_MENTION, {
                        "Channel Name": mentionCard.channel.name,
                        "Channel Type":
                          mentionCard.channel.channel_type?.toLowerCase(),
                        "Episode Title": mentionCard.episode.title,
                        "Mention URL": mentionCard.share_url,
                        Source: mentionCard.channel.website_url,
                      });
                    }}
                  />
                ) : (
                  <Alert className="my-1">
                    <InfoIcon />
                    <AlertTitle>
                      <Trans>
                        Ljudet för det här avsnittet är inte längre tillgängligt
                        i All Ears, men du kan lyssna direkt i källan.
                      </Trans>
                    </AlertTitle>
                  </Alert>
                ))}

              <TranscriptStatus
                hasTranscript={mentionCard.episode.has_transcript}
                transcriptStatus={localTranscriptStatus}
              />

              {mentions.map((mention, i) => (
                <MentionItem
                  groupedTimesAndWords={mentionCard.grouped_times_and_words}
                  channelLanguage={mentionCard.channel.language}
                  mentionsCount={mentionsCount}
                  onAfterDelete={() => {
                    handleAfterDeleteMention(mention.id);
                  }}
                  onAfterTranslation={(translatedText) => {
                    handleAfterTranslateMention(mention.id, translatedText);
                  }}
                  onRevertTranslation={() => {
                    handleRevertMentionTranslation(mention.id);
                  }}
                  onAfterChangeSentiment={(
                    newSentiment,
                    newAggregatedSentiment
                  ) => {
                    handleAfterChangeMentionSentiment(
                      mention.id,
                      newSentiment,
                      newAggregatedSentiment
                    );
                  }}
                  mention={mention}
                  defaultOpen={!i}
                  key={mention.id}
                  index={i}
                />
              ))}
            </div>
          </div>

          <Cover mentionCard={mentionCard} />
        </div>
      </div>

      <MentionCardFooter
        mentionCard={{
          ...mentionCard,
          mentions: localMentions,
          aggregated_sentiment: localSentiment,
          episode: {
            ...mentionCard.episode,
            transcript_status: localTranscriptStatus,
          },
        }}
        onAfterChangeSentiment={handleAfterChangeAggregatedSentiment}
        onAfterDelete={handleAfterDelete}
        onAfterRequestTranscribe={handleAfterRequestTranscribe}
        orgUuid={orgUuid}
        tags={tags}
      />
    </Card>
  );
}

export default function MentionCardNew(props: MentionCardProps) {
  return (
    <MediaProvider>
      <MentionCardNewComponent {...props} />
    </MediaProvider>
  );
}
